import React, { useState } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { Task, TimeEntry } from '../types/models';

function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
}

function totalTime(entries: TimeEntry[]): number {
  return entries
    .filter(e => e.endTime !== null)
    .reduce((sum, e) => sum + (new Date(e.endTime!).getTime() - new Date(e.startTime).getTime()), 0);
}

function toDateTimeLocal(d: Date | null): string {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

interface TimeSheetProps {
  task: Task;
  show: boolean;
  onHide: () => void;
  onAddEntry: (taskId: string, entry: Omit<TimeEntry, 'id'>) => void;
  onDeleteEntry: (taskId: string, entryId: string) => void;
}

export const TimeSheet: React.FC<TimeSheetProps> = ({ task, show, onHide, onAddEntry, onDeleteEntry }) => {
  const emptyForm = { startTime: '', endTime: '', notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const start = new Date(form.startTime);
    const end = form.endTime ? new Date(form.endTime) : null;
    if (end && end <= start) {
      setError('End time must be after start time.');
      return;
    }
    onAddEntry(task.id, {
      taskId: task.id,
      startTime: start,
      endTime: end,
      notes: form.notes,
    });
    setForm(emptyForm);
  };

  const entries = task.timeEntries ?? [];
  const total = totalTime(entries);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Time Sheet — <span className="text-muted fw-normal">{task.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 text-muted">Time Entries</h6>
          <span className="badge bg-primary fs-6">
            Total: {total > 0 ? formatDuration(total) : '0h 00m'}
          </span>
        </div>

        {entries.length === 0 ? (
          <p className="text-muted small">No time entries yet. Add one below.</p>
        ) : (
          <Table size="sm" bordered hover className="mb-4">
            <thead className="table-light">
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Duration</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => {
                const start = new Date(entry.startTime);
                const end = entry.endTime ? new Date(entry.endTime) : null;
                const duration = end ? end.getTime() - start.getTime() : null;
                return (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{start.toLocaleString()}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{end ? end.toLocaleString() : <span className="text-warning">In progress</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{duration !== null ? formatDuration(duration) : '—'}</td>
                    <td>{entry.notes}</td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => onDeleteEntry(task.id, entry.id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <h6 className="mb-3">Add Time Entry</h6>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <Form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.startTime}
                  onChange={e => setForm({ ...form, startTime: e.target.value })}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>End Time <span className="text-muted">(leave blank if still running)</span></Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.endTime}
                  onChange={e => setForm({ ...form, endTime: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Optional notes"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                />
              </Form.Group>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={onHide}>Close</Button>
            <Button variant="primary" type="submit">Add Entry</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
